import { Currency } from 'pixai-contract/payment/const'
import { match } from 'ts-pattern'
import { joinURL, withQuery } from 'ufo'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { openPaymentDialog } from 'src/components/Payment/PaymentDialog'
import { useMyMembership } from 'src/services/apollo/membership'
import {
  PaymentProvider,
  type PaymentExecuteFn,
  type PaymentExecuteInput,
  handlePaymentError,
} from 'src/services/helpers/payment'
import { toWebPaymentBackendProvider } from 'src/services/helpers/paymentProviderMapping'
import { usePurchaseExtraPackageMutation } from 'src/services/orpc/extraPackage'
import { useCurrency } from 'src/utils/currency'
import { track } from 'src/utils/tracker'
import {
  type ExtraPackagePromotionOption,
  ExtraPackageBoostType,
} from './types'
import { useExtraPackages } from './useExtraPackages'

export interface PurchaseExtraPackageReturns {
  orderId: string
}

export interface PurchaseExtraPackageInput {
  extraPackageId: string
  prices: Record<Currency, number>
  redirectUrl?: string
  /**
   * Tracking-only label for analytics. Legacy `useMembershipActions` reads
   * this from the active route's `handle.name`; consumers using this hook
   * directly should pass it explicitly.
   */
  scene?: string
}

/**
 * Map the dialog's frontend `PaymentExecuteInput.provider` onto the oRPC
 * purchase body's `provider` enum. Keeps the conversion centralized so
 * future provider additions don't drift between dialog and backend.
 */
export function toBackendProvider(provider: PaymentExecuteInput['provider']) {
  return toWebPaymentBackendProvider(provider)
}

/**
 * Returns a memoized purchase function for extra packages. Plugs the
 * oRPC purchase mutation into `openPaymentDialog` via `executePayment`;
 * the dialog still owns provider selection, cancel, and error handling.
 */
export function usePurchaseExtraPackage(args: {
  promotionOption: ExtraPackagePromotionOption | null
}) {
  const { promotionOption } = args
  const [currency] = useCurrency()
  const { data: { membership } = {} } = useMyMembership()
  // undefined tier → adapter uses tier-0 boosts (matches legacy behavior).
  const extraPackages = useExtraPackages({
    currentTier: undefined,
    promotionOption,
  })
  const { mutateAsync } = usePurchaseExtraPackageMutation()

  return async function purchase(
    input: PurchaseExtraPackageInput,
  ): Promise<PurchaseExtraPackageReturns | false> {
    if (!extraPackages) return false

    const extraPackage = extraPackages.find(p => p.id === input.extraPackageId)
    if (!extraPackage) return false

    // Only forward promotionOption when the package supports a coupon boost.
    const supportsCoupon = extraPackage.extraPackageBoosts?.some(
      boost => boost.type === ExtraPackageBoostType.Coupon,
    )
    const effectivePromotion =
      supportsCoupon && promotionOption ? promotionOption : undefined
    const couponId = effectivePromotion?.extraPackageBoostCode

    // Final credits after boosts at user's current tier, shown in the dialog.
    const currentTier = membership?.tier ?? 0
    const tierValue = extraPackage.values.find(v => v.tier === currentTier)
    const credits = tierValue?.creditsAfterBoosts

    const redirectUrl = withQuery(
      joinURL(window.location.origin, 'payment/landing'),
      {
        usdPrice: input.prices[Currency.Usd] ?? input.prices[currency],
        redirect: input.redirectUrl,
        couponId,
      },
    )

    // Dialog calls this once the user picks a provider — forward into the oRPC mutation.
    const executePayment: PaymentExecuteFn = (async (
      executeInput: PaymentExecuteInput,
    ) => {
      const providerOptions = match(executeInput)
        .with({ provider: PaymentProvider.gmo }, i => ({
          gmo: {
            paymentMethod: i.providerOptions.paymentMethod,
            phoneNumber: i.providerOptions.phoneNumber,
          },
        }))
        .with({ provider: PaymentProvider.onerwayWechatPay }, i => ({
          // Nested shape matches providerOptionsSchema in pixai-contract.
          onerway: { paymentMethod: i.providerOptions.paymentMethod },
        }))
        .otherwise(() => undefined)

      const { orderId, provider, providerResult } = await mutateAsync({
        skuId: input.extraPackageId,
        provider: toBackendProvider(executeInput.provider),
        promotionOption: effectivePromotion,
        redirectUrl,
        providerOptions,
      })

      const orderIdStr = String(orderId)

      return match(provider)
        .with('onerway', () => {
          const onerway = providerResult?.onerway
          if (!onerway) throw new Error('Onerway result missing')

          // Flatten WeChat QR codeForm: TEXT → qrCode (raw deeplink),
          // IMAGE → qrCodeUrl (hosted image). Card / wallet / wechat_h5
          // flows leave codeForm null — both fall back to undefined.
          const details = onerway.codeForm?.codeDetails ?? []
          const qrCode =
            details.find(
              d => d.codeValueType === 'QRCODE' && d.displayType === 'TEXT',
            )?.codeValue ?? undefined
          const qrCodeUrl =
            details.find(
              d => d.codeValueType === 'QRCODE' && d.displayType === 'IMAGE',
            )?.codeValue ?? undefined

          return {
            orderId: orderIdStr,
            transactionId: onerway.transactionId,
            subProductType: onerway.subProductType,
            // null → undefined (onerway uses null; executor contract uses undefined).
            redirectUrl: onerway.redirectUrl ?? undefined,
            qrCode,
            qrCodeUrl,
          }
        })
        .with('paypay', () => {
          const paypay = providerResult?.paypay
          if (!paypay) throw new Error('PayPay result missing')
          return {
            orderId: orderIdStr,
            url: paypay.url,
            codeId: paypay.codeId,
            deepLink: paypay.deepLink,
          }
        })
        .with('gmo', () => {
          const gmo = providerResult?.gmo
          if (!gmo) throw new Error('GMO result missing')
          return {
            orderId: orderIdStr,
            url: gmo.url,
            confNo: gmo.confNo,
            receiptNo: gmo.receiptNo,
            expiredAt: gmo.expiredAt,
          }
        })
        .otherwise(() => {
          // Mobile/internal providers shouldn't reach the web dialog.
          throw new Error(`Unexpected provider in dialog: ${provider}`)
        })
    }) as PaymentExecuteFn

    try {
      track('begin_checkout', undefined)

      const { orderId, finalizedPaymentProvider } = await openPaymentDialog({
        // With a custom executor, itemId is only used for analytics
        // classification (the `extra-package` token in usePaymentProvider).
        itemId: `extra-package:${input.extraPackageId}`,
        currency,
        prices: input.prices,
        paymentProvider: { allowed: 'any' },
        dialogTrackingOpts: { scene: input.scene },
        trackingFnAfterFetch: () => {
          track('refuel_purchase_submitted')
        },
        // redirect / promotionOption omitted — the executor sends them
        // into the oRPC body itself; the dialog only forwards them for
        // the legacy intentToPay executor.
        credits,
        executePayment,
      })

      track('order_payment_success', {
        type: 'purchase',
        cate: 'refuel',
        currency,
        extraPackageId: input.extraPackageId,
        provider: finalizedPaymentProvider,
        price: input.prices[currency],
        usdPrice: input.prices[Currency.Usd] ?? input.prices[currency],
        scene: input.scene,
        couponId,
      })
      return { orderId }
    } catch (err) {
      if (err !== UserCancelled) {
        handlePaymentError(err)
      }
      return false
    }
  }
}
